import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getopenrouterModelConfig } from './utils';
import type { StatusMapping, openrouterCreateResponse, openrouterTaskResponse } from './types';
import { getValueByPath, isStatusMatch, hasImageInput } from '../tools';

// 创建提供商实例
const provider = new BaseVideoProvider({
    baseUrlEnv: 'OPENROUTER_BASE_URL',
    apiKeyEnv: 'OPENROUTER_API_KEY',
    debugEnv: 'OPENROUTER_DEBUG',
    providerName: 'openrouter',
});

// 实现适配器
const adapter: VideoProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取模型配置
        const config = getopenrouterModelConfig(modelId);
        let apiPath: string;
        if (typeof config.apiPath === 'string') {
            apiPath = config.apiPath;
        } else {
            const frameMode = params.modelUi?.frameMode;
            const hasImage = hasImageInput(params.imagePrompt);
            apiPath = config.apiPath({ frameMode, hasImage, imagePrompt: params.imagePrompt });
        }
        // 构建输入参数（可能是异步构建）
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`, // 特别注意
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: openrouterCreateResponse): CreateTaskResult {
        // 获取服务商配置
        const config = getopenrouterModelConfig(modelId);
        const taskIdPath = config.statusMapping.taskIdPath;
        // 获取任务 ID
        const taskId = getValueByPath(data, taskIdPath);
        if (!taskId) {
            throw new Error(`Failed to get task id from response`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 获取模型配置 (用于确定轮询路径)
        const config = getopenrouterModelConfig(modelId);
        const rawPollApiPath = config.pollApiPath;
        const pollApiPath = typeof rawPollApiPath === 'function'
            ? rawPollApiPath({
                frameMode: params.modelUi?.frameMode,
                hasImage: hasImageInput(params.imagePrompt),
                imagePrompt: params.imagePrompt,
            })
            : rawPollApiPath;
        // 返回请求配置
        return {
            url: (taskId) => {
                const url = pollApiPath.includes('{id}') 
                    ? pollApiPath.replace('{id}', taskId)
                    : `${pollApiPath}/${taskId}`;
                return `${context.baseUrl}${url}`;
            },
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: openrouterTaskResponse): PollTaskResult {
        // 读取服务商配置 (状态映射)
        const config = getopenrouterModelConfig(modelId);
        const statusMapping = config.statusMapping;
        const statePath = statusMapping.statePath;
        // 获取任务状态
        const state = getValueByPath(data, statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }
        // 1. 判断**成功**状态
        if (isStatusMatch(state, statusMapping.success)) {
            // 获取视频 URL 路径
            const videoUrlPath = statusMapping.videoUrlPath;
            // 获取视频 URL
            const videoUrl = getValueByPath(data, videoUrlPath);
            // 视频 URL 不存在则视为**失败**状态
            if (!videoUrl) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing video URL in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                videoUrl: videoUrl,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, statusMapping.failed)) {
            const error = getValueByPath(data, statusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, statusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const openrouter = (modelId: string): VideoModel => {
    const config = getopenrouterModelConfig(modelId);
    return provider.createVideoModel(modelId, adapter);
};

